import { authFetch, request } from "./client";

export interface ComplianceRule {
  rule: string;
  status: "pass" | "warn" | "fail";
  violation_count: number;
  total_checked: number;
  detail: string;
}

export interface DailyInsight {
  id: number;
  playbook_id: string;
  date: string;
  created_at: number;
  session_count: number;
  summary: string;
  call_outcomes?: {
    amd: {
      total_calls: number;
      amd_count: number;
    };
  };
  goal_analysis?: {
    goals: {
      goal: string;
      status: "pass" | "partial" | "fail";
      achieved_count: number;
      total_checked: number;
      achievement_rate: number;
      analysis: string;
    }[];
    overall: string;
  };
  compliance: {
    rules: ComplianceRule[];
    total_violations: number;
    sample_size: number;
  };
  objections: {
    categories: {
      name: string;
      percentage: number;
      count: number;
      representative_quotes: string[];
      current_handling: string;
    }[];
  };
  customer_profile: {
    competitors: { name: string; mentions: number; sentiment: string }[];
    needs: string[];
    decision_roles: string[];
    price_sensitivity: string;
  };
  demand_signals: {
    unmet_needs: { signal: string; evidence: string }[];
    price_anchors: { signal: string; evidence: string }[];
    competitor_pain_points: { signal: string; evidence: string }[];
  };
  recommendations: { title: string; detail: string; priority: string }[];
  stats: {
    total_sessions: number;
    success_rate: number;
    sample_sizes: Record<string, number>;
  };
}

export interface InsightDatesResponse {
  generated_dates: string[];
  available_dates: string[];
}

export function listInsightDates(playbookId: string): Promise<InsightDatesResponse> {
  return request(`/playbooks/${playbookId}/insights`);
}

export function getInsight(playbookId: string, date: string): Promise<DailyInsight> {
  return request(`/playbooks/${playbookId}/insights/${date}`);
}

export function generateInsightSSE(playbookId: string, date: string): Promise<Response> {
  return authFetch(`/playbooks/${playbookId}/insights`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });
}

export interface ImproveAnalyzeResult {
  improve_session_id: string;
  created_at: number;
  session_count: number;
  summary: string;
  strengths: string[];
  weaknesses: string[];
  reject_analysis: string;
  recommendations: { title: string; detail: string }[];
  improved_playbook: string;
  original_playbook: string;
  stats: Record<string, unknown>;
}

export function startImproveSession(id: string): Promise<{ improve_session_id: string }> {
  return request(`/playbooks/${id}/improve/start`, { method: "POST" });
}

export function analyzePlaybookImprove(id: string, date: string): Promise<ImproveAnalyzeResult> {
  return request(`/playbooks/${id}/improve/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });
}

export function listImproveDates(id: string): Promise<InsightDatesResponse> {
  return request(`/playbooks/${id}/improve`);
}

export function getImproveByDate(id: string, date: string): Promise<ImproveAnalyzeResult> {
  return request(`/playbooks/${id}/improve/${date}`);
}

export function commitImprove(id: string, improveSessionId: string, playbookContent: string): Promise<{ ok: boolean }> {
  return request(`/playbooks/${id}/improve/commit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ improve_session_id: improveSessionId, playbook_content: playbookContent }),
  });
}

export function applyImprovement(
  id: string,
  improveSessionId: string,
  currentPlaybook: string,
  messages: Array<{ role: string; content: string }>,
): Promise<{ content: string }> {
  return request(`/playbooks/${id}/improve/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ improve_session_id: improveSessionId, current_playbook: currentPlaybook, messages }),
  });
}
